import type { StoreActionApi } from 'react-sweet-state';

import { type UiPreferencesResponse } from '../../../services/endpoints/page/types';
import { type ReadyStore } from '../state/types';
import { type ContainerProps } from '../types';

const init =
	(sectionCollapsed: boolean, schedulePreferences: UiPreferencesResponse['schedulePreferences']) =>
	({ setState, getState }: StoreActionApi<ReadyStore>, { onAllSchedulesReady }: ContainerProps) => {
		const status = getState().status;

		// Already initialized
		if (status === 'done' || status === 'terminated' || status === 'ready') {
			return;
		}

		// Main schedule is initially collapsed
		if (sectionCollapsed) {
			setState({ status: 'terminated' });
			return;
		}

		// There are no schedules
		if (schedulePreferences.length === 0) {
			setState({ status: 'terminated' });
			return;
		}

		// All schedules are initially collapsed
		if (schedulePreferences.every(({ collapsed }) => collapsed)) {
			setState({
				status: 'done',
				schedules: schedulePreferences.reduce((acc, { id }) => ({ [id]: true, ...acc }), {}),
			});
			onAllSchedulesReady?.();
			return;
		}

		setState({
			status: 'ready',
			schedules: schedulePreferences.reduce(
				(acc, { id, collapsed }) => ({ [id]: collapsed, ...acc }),
				{},
			),
		});
	};

const onScheduleReady =
	(scheduleId: string) =>
	({ setState, getState }: StoreActionApi<ReadyStore>, { onAllSchedulesReady }: ContainerProps) => {
		const status = getState().status;

		// Already finished or not initialized
		if (status === 'done' || status === 'terminated' || status === 'idle') {
			return;
		}

		const schedules = getState().schedules ?? {};

		if (schedules?.[scheduleId] === false) {
			schedules[scheduleId] = true;

			// All schedules are ready
			if (Object.keys(schedules).every((scheduleId) => schedules[scheduleId] === true)) {
				setState({ schedules, status: 'done' });
				onAllSchedulesReady?.();
				return;
			}

			setState({
				schedules,
			});
		}
	};

export const actions = {
	init,
	onScheduleReady,
};
